import React, { useContext } from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { L10NContext } from '@sm/intl';
import { useSurveyTheme } from '@sm/webassets/SurveyTheme/context';

import { SurveyContainerStyle } from '@sm/webassets/SurveyTheme/types';
import { isV2Theme, isOQAATFullBleed } from '~app/helpers/themeLayout';
import { RichText } from '../../core/RichText';
import useSurveyStyles from './useStyles';

/** require `number` if `numbered` is `true` */
type NumberProps =
  | {
      numbered?: false;
      number?: number;
    }
  | {
      numbered: true;
      number: number;
    };

export type Props = {
  id?: Element['id'];
  text: string;
  element?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  isOQAAT?: boolean;
} & NumberProps &
  React.HTMLAttributes<HTMLHeadingElement>;

export type PageTitleStyleProps = {
  surveyContainer?: SurveyContainerStyle;
  isOQAAT: boolean;
};

const useStyles = createUseStyles({
  pageTitleV2Theme: {
    padding: '14px 40px',
    marginTop: -8,
    position: ({ isOQAAT, surveyContainer }: PageTitleStyleProps) =>
      isOQAATFullBleed(surveyContainer, isOQAAT) ? 'relative' : null,
    width: ({ isOQAAT, surveyContainer }: PageTitleStyleProps) =>
      isOQAATFullBleed(surveyContainer, isOQAAT) ? 'calc(100vw - 80px)' : null,
    left: ({ isOQAAT, surveyContainer }: PageTitleStyleProps) =>
      isOQAATFullBleed(surveyContainer, isOQAAT) ? '50%' : null,
    right: ({ isOQAAT, surveyContainer }: PageTitleStyleProps) =>
      isOQAATFullBleed(surveyContainer, isOQAAT) ? '50%' : null,
    marginLeft: ({ isOQAAT, surveyContainer }: PageTitleStyleProps) =>
      isOQAATFullBleed(surveyContainer, isOQAAT) ? '-50vw' : -40,
    marginRight: ({ isOQAAT, surveyContainer }: PageTitleStyleProps) =>
      isOQAATFullBleed(surveyContainer, isOQAAT) ? '-50vw' : -40,
  },
});

const PageTitle: React.FC<Props> = ({
  id,
  element: Element = 'h2',
  text,
  numbered = false,
  number,
  isOQAAT = false,
  ...otherProps
}) => {
  const { isRTL } = useContext(L10NContext);
  const { pageTitle, questionNumber } = useSurveyStyles({ isRTL });
  const theme = useSurveyTheme();
  const { surveyContainer } = theme;

  const { pageTitleV2Theme } = useStyles({ surveyContainer, isOQAAT });
  return (
    <Element
      id={id}
      data-testid="PageTitle__Title"
      className={classNames(pageTitle, isV2Theme(theme.version) ? pageTitleV2Theme : {})}
      {...otherProps}
    >
      {numbered && <span className={questionNumber}>{number}.</span>}
      <RichText element="span" text={text} />
    </Element>
  );
};

export default PageTitle;
